import React, { useState } from 'react';
import styles from "./branchorder.module.css";
import { postFetch } from '../../../fetch/fetch';
import { toast } from 'react-toastify';
import { ChevronIcon } from './shared';

// 쿠팡 주소 → vendorItemId (옵션 ID). 쿠팡 도메인이 아니면 err (물품 수정 모달에서도 사용)
export const parseCoupang = (url: string): { err?: string; vendorItemId?: string } => {
    try {
        const u = new URL(url);
        if (!/(^|\.)coupang\.com$/.test(u.hostname)) return { err: "쿠팡 주소가 아닙니다" };
        return { vendorItemId: u.searchParams.get("vendorItemId") || "" };
    } catch (e) {
        return { err: "주소 형식이 올바르지 않습니다" };
    }
};

export const SERVER_ERRORS: { [key: string]: string } = {
    notCoupang: "쿠팡 주소가 아닙니다",
    wrongUrl: "주소 형식이 올바르지 않습니다",
    noVendorItemId: "옵션 ID(vendorItemId)가 필요합니다",
    DUPLICATE: "이미 등록된 물품입니다",
    noCategory: "분류를 선택해 주세요",
    noLocation: "노출할 지점을 하나 이상 선택해 주세요",
    wrongImage: "이미지 주소는 http(s)로 시작해야 합니다",
    noData: "물품명과 옵션을 입력해 주세요",
    wrongLimit: "하루 한도는 0~9999 사이 숫자로 입력해 주세요",
};

// 관리자 "+ 물품 추가" (이미지는 URL만 — 파일은 저장하지 않음)
const AddProductModal: React.FC<any> = (props: any) => {

    const branches: any[] = props.branches;
    const [url, setUrl] = useState("");
    const [manualId, setManualId] = useState("");
    const [name, setName] = useState("");
    const [option, setOption] = useState("");
    const [category, setCategory] = useState(props.categories.includes(props.defaultCategory) ? props.defaultCategory : (props.categories[0] || "__new"));
    const [newCategory, setNewCategory] = useState("");
    const [unitText, setUnitText] = useState("");
    const [image, setImage] = useState("");
    const [imageFailed, setImageFailed] = useState(false);
    const [note, setNote] = useState("");
    const [dailyLimit, setDailyLimit] = useState("");
    const [vis, setVis] = useState<"all" | "pick">("all");
    const [picked, setPicked] = useState<string[]>(branches.map((b) => b.key));
    const [errors, setErrors] = useState<string[]>([]);
    const [bad, setBad] = useState<{ [key: string]: boolean }>({});
    const [saving, setSaving] = useState(false);

    const parsed = url.trim() ? parseCoupang(url.trim()) : null;
    const vendorItemId = parsed && !parsed.err ? (parsed.vendorItemId || manualId.trim()) : "";
    const isDup = !!vendorItemId && props.existingIds.has(vendorItemId);

    const submit = async (e: any) => {
        e.preventDefault();
        const err: string[] = [];
        const nextBad: { [key: string]: boolean } = {};
        if (!url.trim()) { err.push("쿠팡 상품 링크를 넣어 주세요"); nextBad.url = true; }
        else if (parsed && parsed.err) { err.push(parsed.err); nextBad.url = true; }
        else if (!/^\d{1,20}$/.test(vendorItemId)) { err.push("옵션 ID(vendorItemId)가 필요합니다"); nextBad.id = true; }
        if (isDup) err.push(`옵션 ID ${vendorItemId}는 이미 등록되어 있습니다`);
        if (!name.trim()) { err.push("물품명을 입력해 주세요"); nextBad.name = true; }
        if (!option.trim()) { err.push("옵션을 입력해 주세요"); nextBad.option = true; }
        if (category === "__new" && !newCategory.trim()) { err.push("새 분류 이름을 입력해 주세요"); nextBad.newCategory = true; }
        if (vis === "pick" && !picked.length) err.push("노출할 지점을 하나 이상 선택해 주세요");
        if (dailyLimit.trim() && !/^\d{1,4}$/.test(dailyLimit.trim())) { err.push("하루 한도는 0~9999 사이 숫자로 입력해 주세요"); nextBad.dailyLimit = true; }
        setBad(nextBad);
        setErrors(err);
        if (err.length) return;

        setSaving(true);
        try {
            const result: any = await postFetch("/api/branch-orders/admin/product", {
                url: url.trim(), vendorItemId, name: name.trim(), option: option.trim(),
                category, newCategory: newCategory.trim(), unitText: unitText.trim(), image: image.trim(), note: note.trim(),
                dailyLimit: dailyLimit.trim() === "" ? null : +dailyLimit.trim(),
                visibleBranches: vis === "all" ? "all" : picked,
            });
            if (result.message === "success") {
                toast.success(`"${name.trim()}" 추가 — ${vis === "all" ? "전 지점" : picked.length + "개 지점"} 노출`);
                props.onAdded();
            } else {
                setErrors([SERVER_ERRORS[result.message] || `추가 실패 (${result.message})`]);
            }
        } catch (e) {
            console.log(e);
            setErrors(["추가 실패 — 잠시 후 다시 시도해 주세요"]);
        }
        setSaving(false);
    }

    return (
        <div className={styles.overlay} onClick={(e) => { if (e.target === e.currentTarget && !saving) props.onClose(); }}>
            <div className={`${styles.modal} ${styles.wide}`} role="dialog" aria-modal="true" aria-labelledby="branchOrderAddTitle">
                <div className={styles.modalHeader} id="branchOrderAddTitle">새 물품 추가</div>
                <form id="branchOrderAddForm" className={styles.mb} noValidate onSubmit={submit}>
                    <div className={styles.f}>
                        <label htmlFor="boUrl">쿠팡 상품 링크 <span className={styles.req}>필수</span></label>
                        <input id="boUrl" type="url" className={bad.url || (parsed && parsed.err) ? styles.bad : ""} autoFocus autoComplete="off"
                            placeholder="https://www.coupang.com/vp/products/…?itemId=…&vendorItemId=…" value={url} onChange={(e) => setUrl(e.target.value)} />
                        <div className={styles.fh}>쿠팡 상품 페이지에서 <b>원하는 옵션을 고른 뒤</b> 주소를 복사해 붙여 주세요. 옵션 ID(vendorItemId)가 들어 있어야 선배봇이 같은 옵션을 주문합니다.</div>
                        {
                            parsed && !parsed.err && parsed.vendorItemId ?
                                <div className={styles.idline}><span className={styles.mini}>옵션 ID</span> <span className={styles.num}>{parsed.vendorItemId}</span> {isDup ? <span className={styles.dup}>이미 등록된 물품입니다</span> : null}</div>
                                : null
                        }
                    </div>
                    {
                        parsed && !parsed.err && !parsed.vendorItemId ?
                            <div className={styles.f}>
                                <label htmlFor="boId">옵션 ID (vendorItemId) 직접 입력</label>
                                <input id="boId" type="text" inputMode="numeric" className={bad.id ? styles.bad : ""} placeholder="예) 91953657904" autoComplete="off" value={manualId} onChange={(e) => setManualId(e.target.value)} />
                                <div className={styles.fh}>링크에 옵션 ID가 없습니다. 상품 페이지에서 옵션을 선택하면 주소에 vendorItemId가 생깁니다.{isDup ? <span className={styles.dup}> 이미 등록된 물품입니다</span> : null}</div>
                            </div>
                            : null
                    }
                    <div className={styles.frow}>
                        <div className={styles.f}><label htmlFor="boName">물품명 <span className={styles.req}>필수</span></label><input id="boName" type="text" className={bad.name ? styles.bad : ""} placeholder="예) 한예지 프리미엄 2겹 점보롤 화이트 300m" autoComplete="off" value={name} onChange={(e) => setName(e.target.value)} /></div>
                        <div className={styles.f}><label htmlFor="boOpt">옵션 <span className={styles.req}>필수</span></label><input id="boOpt" type="text" className={bad.option ? styles.bad : ""} placeholder="예) 1개, 16개입" autoComplete="off" value={option} onChange={(e) => setOption(e.target.value)} /><div className={styles.fh}>쿠팡 옵션 표기 그대로 (수량·색상·용량)</div></div>
                    </div>
                    <div className={styles.frow}>
                        <div className={styles.f}>
                            <label htmlFor="boCat">분류 <span className={styles.req}>필수</span></label>
                            <div className={`${styles.selWrap} ${styles.sm}`}>
                                <select id="boCat" value={category} onChange={(e) => setCategory(e.target.value)}>
                                    {props.categories.map((c: string) => <option key={c} value={c}>{c}</option>)}
                                    <option value="__new">+ 새 분류 만들기</option>
                                </select>
                                <ChevronIcon />
                            </div>
                        </div>
                        {
                            category === "__new" ?
                                <div className={styles.f}><label htmlFor="boNewCat">새 분류 이름</label><input id="boNewCat" type="text" className={bad.newCategory ? styles.bad : ""} placeholder="예) 주방용품" autoComplete="off" value={newCategory} onChange={(e) => setNewCategory(e.target.value)} /></div>
                                : null
                        }
                        <div className={styles.f}><label htmlFor="boUnit">주문 단위 설명 <span className={styles.optLabel}>선택</span></label><input id="boUnit" type="text" placeholder="예) 1개 주문 = 16롤 1박스" autoComplete="off" value={unitText} onChange={(e) => setUnitText(e.target.value)} /><div className={styles.fh}>직원이 수량을 헷갈리지 않게 카드에 표시</div></div>
                    </div>
                    <div className={styles.f}>
                        <label htmlFor="boImg">이미지 <span className={styles.optLabel}>선택</span></label>
                        <div className={styles.imgrow}>
                            <div className={styles.imgprev}>
                                {image.trim() && !imageFailed ? <img src={image.trim()} alt="" referrerPolicy="no-referrer" onError={() => setImageFailed(true)} /> : <span className={styles.imgprevPh}>{image.trim() ? "불러오기 실패" : "미리보기"}</span>}
                            </div>
                            <div className={styles.imgctl}>
                                <input id="boImg" type="url" placeholder="이미지 주소 (쿠팡 썸네일 주소 붙여넣기)" autoComplete="off" value={image} onChange={(e) => { setImage(e.target.value); setImageFailed(false); }} />
                                <div className={styles.fh}>비워두면 분류명 박스로 표시</div>
                            </div>
                        </div>
                    </div>
                    <div className={styles.f}>
                        <label htmlFor="boNote">주문 시 참고 <span className={styles.optLabel}>선택</span></label>
                        <input id="boNote" type="text" placeholder="예) 로켓배송만 / 와우 할인가로 / 대체 상품 불가" autoComplete="off" value={note} onChange={(e) => setNote(e.target.value)} />
                        <div className={styles.fh}>선배봇에게 보내는 슬랙 메시지에 함께 전달됩니다</div>
                    </div>
                    <div className={styles.f}>
                        <label htmlFor="boLimit">하루 주문 한도 <span className={styles.optLabel}>선택</span></label>
                        <input id="boLimit" type="number" min={0} max={9999} className={bad.dailyLimit ? styles.bad : ""} placeholder="비우면 무제한" autoComplete="off" value={dailyLimit} onChange={(e) => setDailyLimit(e.target.value)} />
                        <div className={styles.fh}>지점별 하루 주문 수량 기준 (예: 2 → 각 지점 하루 2개까지)</div>
                    </div>
                    <div className={styles.f}>
                        <label>노출 지점</label>
                        <div className={styles.visopts}>
                            <label className={styles.radio}><input type="radio" name="boVis" checked={vis === "all"} onChange={() => setVis("all")} /> 전 지점 노출</label>
                            <label className={styles.radio}><input type="radio" name="boVis" checked={vis === "pick"} onChange={() => setVis("pick")} /> 선택한 지점만</label>
                        </div>
                        {
                            vis === "pick" ?
                                <div className={styles.chkGrid}>
                                    {branches.map((b) => (
                                        <label key={b.key}><input type="checkbox" checked={picked.includes(b.key)}
                                            onChange={(e) => setPicked(e.target.checked ? [...picked, b.key] : picked.filter((k) => k !== b.key))} /> {b.label}</label>
                                    ))}
                                </div>
                                : null
                        }
                    </div>
                    {errors.length ? <div className={styles.ferr}>{errors.map((m) => <div key={m}>{m}</div>)}</div> : null}
                </form>
                <div className={styles.modalFooter}>
                    <button type="button" className={styles.btn} disabled={saving} onClick={props.onClose}>취소</button>
                    <button type="submit" form="branchOrderAddForm" className={`${styles.btn} ${styles.primary}`} disabled={saving}>{saving ? "추가 중…" : "추가"}</button>
                </div>
            </div>
        </div>
    );
}

export default AddProductModal;
